import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ActionsPanel, Cell, Confirm, DataTable, Empty, FlowEditor, NameAFlow, PERMISSION,
  EntryCondition, botApi, can, channelName, describeEntry, formatIst, groupBotVersions, inboxApi, parseFlow, useAuth,
  type BotEntry, type BotFlowSummary, type BotGroup,
} from '@wa/shared';

/**
 * Bots, in the client's own portal.
 *
 * The same builder PSM uses, against routes the server scopes to this tenant. Both
 * doors exist on purpose: a client building their own bot is the point, and PSM
 * building one for them is how a client gets started and how a broken one gets fixed.
 *
 * The page opens by saying where a bot sits rather than listing flows, because the
 * question somebody arrives with is not "what flows are there" — it is "will this
 * answer before my agents do?".
 *
 * The open bot is in the URL (/bots/:id) rather than in state, so a refresh, a
 * shared link and the browser's back button all do what they look like they do.
 *
 * `pane-scroll h-full overflow-y-auto` is not decoration. AppShell's <main> is
 * `overflow-hidden`, so a page that does not carry its own scroller simply loses
 * everything below the fold. And there is no max-width: this is three tables of
 * addresses and timestamps, and capping it drew a column down the middle of a
 * 1900px display with a third of the screen empty on either side.
 */
export function BotsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { id: idParam } = useParams();
  const openFlowId = idParam ? Number(idParam) || null : null;
  const queryClient = useQueryClient();
  const [naming, setNaming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<BotFlowSummary | null>(null);

  // The entry condition is written to every version of the bot, so the whole
  // list is refetched rather than one row patched in place.
  const setEntry = useMutation({
    mutationFn: ({ id, entry }: { id: number; entry: BotEntry }) => botApi.setFlowEntry(id, entry),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['bot-flows'] }),
  });

  const mayEdit = can(user, PERMISSION.automationWrite);
  const mayHoldCredentials = can(user, PERMISSION.settingsWrite);

  const flows = useQuery({ queryKey: ['bot-flows'], queryFn: botApi.flows });
  const actions = useQuery({ queryKey: ['bot-actions'], queryFn: botApi.actions });
  const sessions = useQuery({ queryKey: ['bot-sessions'], queryFn: botApi.sessions, refetchInterval: 30_000 });

  // Suggestions for the steps that refer to something by name. Each list is a screen
  // the client already has, so the step's field can stay a text box with a datalist
  // rather than a dropdown that is empty until a second request finishes.
  const tags = useQuery({ queryKey: ['tags'], queryFn: inboxApi.tags, staleTime: 300_000 });
  const templates = useQuery({ queryKey: ['templates'], queryFn: inboxApi.templates, staleTime: 300_000 });
  const channels = useQuery({ queryKey: ['channels'], queryFn: inboxApi.channels, enabled: openFlowId !== null });

  const open = useQuery({
    queryKey: ['bot-flow', openFlowId],
    queryFn: () => botApi.flow(openFlowId!),
    enabled: openFlowId !== null,
  });

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['bot-flows'] });
    void queryClient.invalidateQueries({ queryKey: ['bot-sessions'] });
  };

  const act = (fn: () => Promise<unknown>, fallback: string) => async () => {
    setError(null);
    try {
      await fn();
      refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : fallback);
    }
  };

  const create = useMutation({
    mutationFn: (name: string) => botApi.createFlow({ name }),
    onSuccess: (created) => {
      setError(null);
      setNaming(false);
      refresh();
      navigate(`/bots/${created.id}`);
    },
    onError: (e) => setError(e instanceof Error ? e.message : 'Could not create that bot.'),
  });

  // A copy starts as a draft with the same steps, on no number: two bots cannot both
  // be live on one, and the copy is for changing, not for answering.
  const duplicate = useMutation({
    mutationFn: async (flow: BotFlowSummary) => {
      const { flow: full } = await botApi.flow(flow.id);
      return botApi.createFlow({ name: `${flow.name} (copy)`, definition: full.definition });
    },
    onSuccess: (created) => { setError(null); refresh(); navigate(`/bots/${created.id}`); },
    onError: (e) => setError(e instanceof Error ? e.message : 'Could not copy that bot.'),
  });

  const remove = useMutation({
    mutationFn: (id: number) => botApi.deleteFlow(id),
    onSuccess: () => { setError(null); refresh(); },
    onError: (e) => setError(e instanceof Error ? e.message : 'Could not delete that draft.'),
  });

  if (openFlowId !== null) {
    // Only a bot that was never published can be deleted — the server refuses the
    // rest, so the button is not offered for them either.
    const deletable = groupBotVersions(flows.data ?? []).some((b) => !b.published && b.draft?.id === openFlowId);
    if (open.isError) {
      return (
        <div className="pane-scroll h-full overflow-y-auto bg-(--color-paper)">
          <div className="p-4 sm:p-6 lg:p-8">
            <Empty>That bot does not exist, or is not yours.</Empty>
            <button type="button" onClick={() => navigate('/bots')} className="mt-3 rounded-lg border border-(--color-line) px-3.5 py-2 text-body">← All bots</button>
          </div>
        </div>
      );
    }
    if (!open.data) return <div className="h-full bg-(--color-paper)" />;

    return (
      <div className="pane-scroll h-full overflow-y-auto bg-(--color-paper)">
        <div className="p-4 sm:p-6 lg:p-8">
          <FlowEditor
            key={open.data.flow.id}
            flow={open.data.flow}
            actions={actions.data ?? []}
            channels={(channels.data ?? []).filter((c) => c.isActive).map((c) => ({
              id: c.id, label: channelName(c),
            }))}
            api={{
              validate: (definition) => botApi.validateFlow(definition),
              save: (id, patch) => botApi.saveFlow(id, patch),
              publish: (id) => botApi.publishFlow(id),
              rehearse: (body) => botApi.rehearseFlow(body),
              remove: mayEdit && deletable ? (id) => botApi.deleteFlow(id) : undefined,
              testAction: mayEdit ? botApi.testAction : undefined,
              products: () => inboxApi.catalogProducts().then((r) => r.items),
              names: {
                templates: (templates.data ?? [])
                  .filter((t) => (t.status ?? '').toUpperCase() === 'APPROVED')
                  .map((t) => t.name),
                tags: (tags.data ?? []).map((t) => t.name),
                // Every other live bot. Its own name is left out: a bot that jumps to
                // itself is a restart written the long way round, and the engine
                // refuses it.
                flows: [...new Set((flows.data ?? [])
                  .filter((f) => f.isEnabled && f.name !== open.data!.flow.name)
                  .map((f) => f.name))],
              },
            }}
            onClose={() => { refresh(); navigate('/bots'); }}
          />
        </div>
      </div>
    );
  }

  const rows = flows.data ?? [];
  const bots = groupBotVersions(rows);
  const live = rows.filter((f) => f.isEnabled);

  return (
    <div className="pane-scroll h-full overflow-y-auto bg-(--color-paper)">
      <div className="flex flex-col gap-6 p-4 sm:p-6 lg:p-8">
        <header>
          <h1 className="text-title font-semibold tracking-tight">Bots</h1>
          <p className="mt-1.5 max-w-[70ch] text-body text-(--color-ink-soft)">
            A bot answers first. While one is running on a conversation your routing
            rules are held, and an agent is chosen at the moment the bot hands over —
            so nobody is given a chat that a robot is still in the middle of.
          </p>
        </header>

        <WhatIsLive live={live} rows={rows} />

        {live.length > 0 && <HowItsGoing names={[...new Set(live.map((f) => f.name))]} />}

        {error && (
          <p role="alert" className="rounded-lg border border-(--color-bad) bg-(--color-bad-wash) px-4 py-3 text-body text-(--color-bad)">
            {error}
          </p>
        )}

        <section>
          <div className="mb-3 flex flex-wrap items-center gap-3">
            <h2 className="text-lead font-semibold">Your bots</h2>
            {mayEdit && !naming && (
              <button
                type="button"
                onClick={() => { setError(null); setNaming(true); }}
                className="ml-auto rounded-lg bg-(--color-brand) px-4 py-2 text-body font-medium text-(--color-brand-ink) transition hover:bg-(--color-brand-strong)"
              >
                New bot
              </button>
            )}
          </div>

          {naming && (
            <NameAFlow
              busy={create.isPending}
              onCancel={() => setNaming(false)}
              onCreate={(name) => create.mutate(name)}
            />
          )}

          {bots.length === 0 ? (
            <Empty>
              No bots yet. A new one starts as a single welcome message you can build out.
            </Empty>
          ) : (
            <>
              {/* One line per bot, not per version. The API returns versions — v1
                  published, v2 the draft opened from it — and two rows with the same
                  name said nothing about which one to open. Cards below sm, because a
                  seven-column table inside a scroller puts the buttons off-screen. */}
              <ul className="flex flex-col gap-2 sm:hidden">
                {bots.map((bot) => (
                  <li key={bot.key} className="rounded-lg border border-(--color-line) bg-(--color-surface) p-4">
                    <div className="flex items-center gap-2">
                      <button type="button" onClick={() => navigate(`/bots/${(bot.draft ?? bot.versions[0]!).id}`)} className="min-w-0 truncate text-lead font-medium text-(--color-brand)">
                        {bot.name}
                      </button>
                      <BotState bot={bot} />
                    </div>
                    <p className="mt-1.5 text-body text-(--color-muted)">
                      Answers on {bot.channelNumber ?? 'every number'}
                      {bot.liveSessions ? ` · ${bot.liveSessions} in it now` : ''}
                    </p>
                    <p className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-small">
                      {bot.versions.map((v) => <VersionLink key={v.id} version={v} onOpen={(id) => navigate(`/bots/${id}`)} />)}
                    </p>
                    <div className="mt-2 text-small">
                      <EntryCondition
                        flow={bot.live ?? bot.published ?? bot.versions[0]!}
                        busy={setEntry.isPending}
                        compact={!mayEdit}
                        onSave={(entry) => setEntry.mutate({ id: (bot.live ?? bot.published ?? bot.versions[0]!).id, entry })}
                      />
                    </div>
                    {mayEdit && (
                      <div className="mt-3 grid grid-cols-2 gap-2">
                        <LiveToggle bot={bot} act={act} className="rounded-lg border border-(--color-line) py-2 text-body" />
                        <button type="button" onClick={() => duplicate.mutate(bot.draft ?? bot.versions[0]!)} disabled={duplicate.isPending} className="rounded-lg border border-(--color-line) py-2 text-body">
                          Duplicate
                        </button>
                        {!bot.published && bot.draft && (
                          <button type="button" onClick={() => setDeleting(bot.draft!)} className="col-span-2 rounded-lg border border-(--color-line) py-2 text-body text-(--color-muted)">
                            Delete
                          </button>
                        )}
                      </div>
                    )}
                  </li>
                ))}
              </ul>

              <div className="hidden sm:block">
                <DataTable head={['Name', 'Answers when', 'Answers on', 'State', 'Versions', 'In it now', '']}>
                  {bots.map((bot) => (
                    <tr key={bot.key} className="border-t border-(--color-line-dim)">
                      <Cell>
                        <button type="button" onClick={() => navigate(`/bots/${(bot.draft ?? bot.versions[0]!).id}`)} className="text-body font-medium text-(--color-brand) hover:underline">
                          {bot.name}
                        </button>
                      </Cell>
                      <Cell>
                        <EntryCondition
                          flow={bot.live ?? bot.published ?? bot.versions[0]!}
                          busy={setEntry.isPending}
                          compact={!mayEdit}
                          onSave={(entry) => setEntry.mutate({ id: (bot.live ?? bot.published ?? bot.versions[0]!).id, entry })}
                        />
                      </Cell>
                      <Cell nowrap>{bot.channelNumber ?? 'Every number'}</Cell>
                      <Cell><BotState bot={bot} /></Cell>
                      <Cell>
                        <span className="flex flex-wrap gap-x-3 gap-y-1">
                          {bot.versions.map((v) => <VersionLink key={v.id} version={v} onOpen={(id) => navigate(`/bots/${id}`)} />)}
                        </span>
                      </Cell>
                      <Cell mono>{bot.liveSessions || ''}</Cell>
                      <Cell nowrap>
                        {mayEdit && (
                          <span className="flex justify-end gap-1.5">
                            <LiveToggle bot={bot} act={act} className={ROW_BUTTON} />
                            <button type="button" onClick={() => duplicate.mutate(bot.draft ?? bot.versions[0]!)} disabled={duplicate.isPending} className={ROW_BUTTON}>
                              Duplicate
                            </button>
                            {!bot.published && bot.draft && (
                              <button type="button" onClick={() => setDeleting(bot.draft!)} className={`${ROW_BUTTON} text-(--color-muted)`}>
                                Delete
                              </button>
                            )}
                          </span>
                        )}
                      </Cell>
                    </tr>
                  ))}
                </DataTable>
              </div>
            </>
          )}
          <p className="mt-2.5 max-w-[90ch] text-small leading-relaxed text-(--color-muted)">
            As many bots can be live as you like. Each says when it answers, and they
            are tried in order — the first one that matches replies. Somebody already
            part-way through a bot stays in it, so a keyword bot only starts a
            conversation, it does not interrupt one. A draft is yours to edit;
            publishing freezes that version and opens the next, so a change never
            lands on somebody mid-answer.
          </p>
        </section>

        {mayHoldCredentials ? (
          <ActionsPanel
            actions={actions.data ?? []}
            api={{ save: botApi.saveAction, remove: botApi.deleteAction, test: mayEdit ? botApi.testAction : undefined }}
          />
        ) : (
          (actions.data ?? []).length > 0 && (
            <section>
              <h2 className="mb-2 text-lead font-semibold">Actions</h2>
              <p className="max-w-[90ch] text-body text-(--color-muted)">
                Your bots can call {actions.data!.length} registered{' '}
                {actions.data!.length === 1 ? 'system' : 'systems'}. Adding one means
                typing a credential, so it needs the Settings permission.
              </p>
            </section>
          )
        )}

        <section>
          <h2 className="mb-1.5 text-lead font-semibold">In a bot right now</h2>
          <p className="mb-3 max-w-[90ch] text-small text-(--color-muted)">
            The first place to look when a bot misbehaves: which step somebody is on,
            and how far they got. Refreshes every half minute.
          </p>
          {(sessions.data ?? []).length === 0 ? (
            <Empty>Nobody is mid-flow.</Empty>
          ) : (
            <div className="overflow-x-auto">
              <DataTable head={['Customer', 'Bot', 'Step', 'Steps taken', 'Last activity']}>
                {(sessions.data ?? []).map((s) => (
                  <tr key={s.conversationId} className="border-t border-(--color-line-dim)">
                    <Cell>
                      <button type="button" onClick={() => navigate(`/chats?open=${s.conversationId}`)} className="text-(--color-brand) hover:underline">
                        {s.contactName ?? `+${s.waId}`}
                      </button>
                    </Cell>
                    <Cell>{s.flowName} v{s.flowVersion}</Cell>
                    <Cell mono>{s.state === '__HUMAN__' ? 'with a person' : s.state === '__END__' ? 'ended' : s.state}</Cell>
                    <Cell mono>{s.stepCount}</Cell>
                    <Cell nowrap>{formatIst(s.updatedAt)}</Cell>
                  </tr>
                ))}
              </DataTable>
            </div>
          )}
        </section>
      </div>

      {deleting && (
        <Confirm
          title={`Delete ${deleting.name}?`}
          body="It was never published, so nothing else refers to it. This cannot be undone."
          confirm="Delete"
          danger
          onCancel={() => setDeleting(null)}
          onConfirm={() => { const id = deleting.id; setDeleting(null); remove.mutate(id); }}
        />
      )}
    </div>
  );
}

const ROW_BUTTON = 'rounded-lg border border-(--color-line) px-3 py-1.5 text-small whitespace-nowrap transition hover:bg-(--color-sunken) disabled:opacity-40';

function BotState({ bot }: { bot: BotGroup }) {
  const tone = bot.live
    ? 'bg-(--color-good-wash) font-medium text-(--color-good)'
    : bot.published
      ? 'border border-(--color-line) text-(--color-ink-soft)'
      : 'border border-dashed border-(--color-line) text-(--color-muted)';
  return (
    <span className={`rounded-full px-2.5 py-1 text-small whitespace-nowrap ${tone}`}>
      {bot.live ? 'Live' : bot.published ? 'Not live' : 'Draft only'}
    </span>
  );
}

/** One version: "v2 · draft" or "v1 · live since 1 Sept". Opens it. */
function VersionLink({ version, onOpen }: { version: BotFlowSummary; onOpen: (id: number) => void }) {
  const label = version.isEnabled
    ? `live since ${formatIst(version.publishedAt!)}`
    : version.isPublished
      ? `published ${formatIst(version.publishedAt!)}`
      : 'draft';
  return (
    <button type="button" onClick={() => onOpen(version.id)} className="whitespace-nowrap text-small text-(--color-ink-soft) hover:text-(--color-brand) hover:underline">
      <span className="font-mono font-medium text-(--color-ink)">v{version.version}</span> · {label}
    </button>
  );
}

/** Put live / Take offline for the bot's newest published version; nothing for a draft-only bot. */
function LiveToggle({ bot, act, className }: {
  bot: BotGroup;
  act: (fn: () => Promise<unknown>, fallback: string) => () => Promise<void>;
  className: string;
}) {
  const target = bot.live ?? bot.published;
  if (!target) return <button type="button" disabled title="Publish a version first" className={className}>Put live</button>;
  return (
    <button
      type="button"
      onClick={act(() => (target.isEnabled ? botApi.disableFlow(target.id) : botApi.enableFlow(target.id)), 'Could not change that.')}
      className={className}
    >
      {target.isEnabled ? 'Take offline' : `Put v${target.version} live`}
    </button>
  );
}

/**
 * What will actually happen to the next message.
 *
 * Reading a list of bots does not answer it — some are live, the rest are not, and
 * a bot on its own number and a bot on every number are both "live" but not for the
 * same customers. The warning underneath is the expensive one: a live bot with no
 * hand-over step means routing is never consulted, so no agent is ever given a
 * conversation it touched. Checked here from the definition rather than waiting for
 * somebody to open the editor.
 */
/**
 * How the live bots are actually doing.
 *
 * The one question a client asks about a bot that the product could not answer: of the
 * people who talked to it, how many got what they came for and where did the rest stop.
 * Everything here is one row per finished conversation — no per-step event log — so it
 * costs a single read per bot.
 *
 * It says nothing at all until there is something to say. A panel of zeros on the day a
 * bot goes live reads as a broken bot rather than a new one.
 */
function HowItsGoing({ names }: { names: string[] }) {
  const [days, setDays] = useState(30);
  const [which, setWhich] = useState(names[0] ?? '');
  const name = names.includes(which) ? which : (names[0] ?? '');

  const funnel = useQuery({
    queryKey: ['bot-funnel', name, days],
    queryFn: () => botApi.funnel(name, days),
    enabled: name.length > 0,
  });

  if (!name) return null;

  const data = funnel.data;
  const reachedTop = data?.reached[0]?.count ?? 0;

  return (
    <section className="rounded-xl border border-(--color-line) bg-(--color-surface) p-4 sm:p-5">
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="text-lead font-semibold">How it is going</h2>

        {names.length > 1 && (
          <label className="text-small text-(--color-muted)">
            <span className="sr-only">Which bot</span>
            <select
              value={name}
              onChange={(e) => setWhich(e.target.value)}
              className="ml-1 rounded-lg border border-(--color-line) bg-(--color-paper) px-2 py-1 text-small"
            >
              {names.map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
          </label>
        )}

        <div className="ml-auto flex gap-1">
          {[7, 30, 90].map((d) => (
            <button
              key={d}
              type="button"
              onClick={() => setDays(d)}
              aria-pressed={days === d}
              className={`rounded-lg px-2.5 py-1 text-small ${days === d ? 'bg-(--color-brand-wash) font-medium text-(--color-brand)' : 'text-(--color-muted) hover:text-(--color-ink)'}`}
            >
              {d} days
            </button>
          ))}
        </div>
      </div>

      {funnel.isPending ? (
        <p className="mt-3 text-body text-(--color-muted)">Counting…</p>
      ) : !data || data.total === 0 ? (
        <p className="mt-3 text-body text-(--color-muted)">
          No finished conversations in the last {days} days yet. This fills in as customers talk to it —
          nothing before today was recorded, so a bot that has been live a while starts from here too.
        </p>
      ) : (
        <>
          <dl className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-4">
            {([
              ['Conversations', data.total, null],
              ['Finished the flow', data.completed, 'good'],
              ['Passed to a person', data.handedOff, null],
              ['Stopped part-way', data.leftEarly, data.leftEarly > 0 ? 'bad' : null],
            ] as const).map(([label, value, tone]) => (
              <div key={label}>
                <dt className="text-small text-(--color-muted)">{label}</dt>
                <dd className={`text-lead font-semibold ${tone === 'bad' ? 'text-(--color-bad)' : tone === 'good' ? 'text-(--color-good)' : ''}`}>
                  {value.toLocaleString('en-IN')}
                  {label !== 'Conversations' && data.total > 0 && (
                    <span className="ml-1 text-small font-normal text-(--color-muted)">
                      {Math.round((value / data.total) * 100)}%
                    </span>
                  )}
                </dd>
              </div>
            ))}
          </dl>

          <p className="mt-3 text-small text-(--color-muted)">
            Typically {data.medianTurns} {data.medianTurns === 1 ? 'reply' : 'replies'} from the customer per conversation.
          </p>

          {data.dropOff.length > 0 && (
            <div className="mt-5">
              <h3 className="text-body font-medium">Where people stop</h3>
              <p className="mt-0.5 text-small text-(--color-muted)">
                The step they were on when they went quiet, or when the bot gave up on their answer.
                A step near the top of this list is usually a question worth rewording.
              </p>
              <ul className="mt-2 flex flex-col gap-1.5">
                {data.dropOff.map((d) => (
                  <li key={d.node} className="flex items-center gap-3 text-body">
                    <span className="min-w-0 flex-1 truncate font-mono text-small">{d.node}</span>
                    <span className="tabular-nums text-(--color-muted)">{d.count}</span>
                    <span
                      className="h-1.5 w-24 shrink-0 rounded-full bg-(--color-sunken)"
                      role="img"
                      aria-label={`${d.count} of ${data.leftEarly}`}
                    >
                      <span
                        className="block h-full rounded-full bg-(--color-bad)"
                        style={{ width: `${Math.max(6, Math.round((d.count / Math.max(1, data.dropOff[0]!.count)) * 100))}%` }}
                      />
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {data.reached.length > 1 && (
            <details className="mt-5">
              <summary className="cursor-pointer text-body font-medium">How far people get</summary>
              <ul className="mt-2 flex flex-col gap-1.5">
                {data.reached.map((r) => (
                  <li key={r.node} className="flex items-center gap-3 text-body">
                    <span className="min-w-0 flex-1 truncate font-mono text-small">{r.node}</span>
                    <span className="tabular-nums text-(--color-muted)">{r.count}</span>
                    <span className="h-1.5 w-24 shrink-0 rounded-full bg-(--color-sunken)">
                      <span
                        className="block h-full rounded-full bg-(--color-brand)"
                        style={{ width: `${Math.max(6, Math.round((r.count / Math.max(1, reachedTop)) * 100))}%` }}
                      />
                    </span>
                  </li>
                ))}
              </ul>
            </details>
          )}
        </>
      )}
    </section>
  );
}

function WhatIsLive({ live, rows }: { live: BotFlowSummary[]; rows: BotFlowSummary[] }) {
  const ids = live.map((f) => f.id);
  const details = useQuery({
    queryKey: ['bot-flows-live', ids],
    queryFn: () => Promise.all(ids.map((id) => botApi.flow(id))),
    enabled: ids.length > 0,
  });

  const silent = (details.data ?? []).filter(({ flow }) =>
    !Object.values(parseFlow(flow.definition).nodes ?? {}).some((node) => node?.type === 'handoff'));

  // The order the engine reads them in, which is the order a person has to think
  // in to answer "who replies to this?".
  const ordered = [...live].sort((a, b) => (a.priority ?? 100) - (b.priority ?? 100) || a.id - b.id);
  const catchAll = ordered.some((f) => (f.triggerKind ?? 'always') === 'always');

  if (live.length === 0) {
    return (
      <p className="rounded-lg border border-(--color-line) bg-(--color-surface) px-4 py-3.5 text-body text-(--color-ink-soft)">
        {rows.length === 0
          ? 'Nothing answers automatically. Every message goes straight to your routing rules.'
          : 'None of your bots is live, so every message goes straight to your routing rules.'}
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-2.5">
      <div className="rounded-lg border border-(--color-line) bg-(--color-surface) px-4 py-3.5 text-body text-(--color-ink-soft)">
        <p className="mb-1.5 text-small text-(--color-muted)">
          A new message is offered to each of these in turn. The first one that matches answers it.
        </p>
        <ol className="flex flex-col gap-1">
          {ordered.map((flow, i) => (
            <li key={flow.id} className="flex gap-2">
              <span className="w-5 shrink-0 text-right font-mono text-small text-(--color-muted)">{i + 1}.</span>
              <span className="min-w-0">
                <b className="font-semibold text-(--color-ink)">{flow.name}</b> answers {describeEntry(flow)}
                {flow.channelNumber ? <span className="text-(--color-muted)"> · only on {flow.channelNumber}</span> : null}
                {flow.liveSessions ? <span className="text-(--color-muted)"> · {flow.liveSessions} in it now</span> : null}
              </span>
            </li>
          ))}
        </ol>
        <p className="mt-1.5 text-small text-(--color-muted)">Your routing rules run when a bot hands over — or straight away if it fails.</p>
      </div>

      {/* Live bots, none of which takes what the others left. The customer who
          says none of the magic words gets silence, and nothing else in the
          product would ever say so. */}
      {!catchAll && (
        <p className="rounded-lg border border-(--color-warn) bg-(--color-warn-wash) px-4 py-3.5 text-body text-(--color-ink-soft)">
          <b className="font-semibold">Nothing answers “anything else”.</b> Every live bot is
          waiting for a keyword or for you to be closed, so a customer who says something
          you did not list gets no reply at all — the chat goes to your routing rules
          instead. Set one bot to <span className="font-mono text-small">Anything else</span>{' '}
          if that is not what you want.
        </p>
      )}

      {silent.map(({ flow }) => (
        <p key={flow.id} className="rounded-lg border border-(--color-warn) bg-(--color-warn-wash) px-4 py-3.5 text-body text-(--color-ink-soft)">
          <b className="font-semibold">{flow.name} has no hand-over step.</b> It can only
          ever say what it knows how to say — no conversation it answers will reach one
          of your agents. Open it and add a <span className="font-mono text-small">Hand to a person</span>{' '}
          step on the paths where somebody should take over.
        </p>
      ))}
    </div>
  );
}
